<template>
  <div class="product-hub">
    <!-- 复用的既有弹窗（新增/编辑、识别图上传/查看、分类、标签） -->
    <add-product
      :dialogProduct="dialogProduct"
      v-on:cancel="cancelProductDialog"
      @use-master="onUseMaster"
      :productTags="productTags"
      :product="product"
      :fromMaterial="fromMaterial"
    >
    </add-product>
    <UploadImgs
      @close="closeUploadImg"
      @refresh="refreshUploadImg"
      :show="showUploadImg"
      :isReUpload="isReUpload"
      :product="productItem"
    />
    <LookImgs @close="closeLookImg" :show="showLookImg" :product="productItem" />
    <add-product-type v-on:cancel="cancelTypeDialog" :productType="productType" :dialogProductType="dialogProductType">
    </add-product-type>
    <add-product-tag v-on:cancel="cancelTagDialog" :productTag="productTag" :dialogProductTag="dialogProductTag">
    </add-product-tag>

    <ProductSimilarDialog
      :show="showSimilarDialog"
      :product="similarProduct"
      @close="closeSimilarDialog"
      @refresh="refreshSimilarProduct"
    />

    <!-- 先在主库中查找（引导优先复用，而非新建） -->
    <product-finder-dialog
      :visible.sync="finderVisible"
      :owned-names="ownedNames"
      @obtained="onFinderObtained"
      @create-new="createNewProduct"
    ></product-finder-dialog>

    <!-- 页头 -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("productHub.title") }}</h1>
        <div class="vtm-page-head__sub">{{ $t("productHub.subtitle") }}</div>
      </div>
      <div class="hub-head-actions">
        <el-button size="small" icon="el-icon-collection-tag" @click="organizeVisible = true">
          {{ $t("productHub.organize") }}
        </el-button>
      </div>
    </div>

    <!-- 模式切换：我的商品 | 素材库 -->
    <div class="hub-seg-row">
      <el-radio-group v-model="mode" size="small" class="hub-seg">
        <el-radio-button label="mine">
          <i class="el-icon-box"></i> {{ $t("productHub.tabMine") }}
          <span class="hub-seg__count">{{ minePage.totalSize || 0 }}</span>
        </el-radio-button>
        <el-radio-button label="catalog">
          <i class="el-icon-collection"></i> {{ $t("productHub.tabCatalog") }}
          <span class="hub-seg__count">{{ catalogPage.totalSize || 0 }}</span>
        </el-radio-button>
      </el-radio-group>
    </div>

    <!-- ===================== 我的商品（图库式浏览，与素材库保持一致的视觉风格） ===================== -->
    <list-page
      v-if="mode === 'mine'"
      :loading="mineLoading"
      :error="mineError"
      :empty="!productList.length"
      :empty-text="$t('productHub.emptyMine')"
      :total="minePage.totalSize || 0"
      :page-index="minePage.pageIndex"
      :page-size="minePage.pageSize"
      :page-sizes="[12, 24, 48, 96]"
      @page-change="loadMine"
      @size-change="mineSizeChange"
      @retry="loadMine(minePage.pageIndex)"
    >
      <template #head-actions>
        <el-button type="primary" size="small" v-hasPerm="['product:insert']" icon="el-icon-plus" @click="addProduct">
          {{ $t("common.add") }}
        </el-button>
        <el-button size="small" icon="el-icon-collection" @click="mode = 'catalog'">
          {{ $t("productHub.browseCatalog") }}
        </el-button>
      </template>

      <template #filters>
        <div class="hub-catalog__toolbar">
          <el-input
            v-model="mineSearch.name"
            size="small"
            clearable
            class="hub-catalog__search"
            prefix-icon="el-icon-search"
            :placeholder="$t('productHub.mineSearch')"
            @input="debouncedMineSearch"
            @clear="loadMine(1)"
            @keyup.enter.native="loadMine(1)"
          ></el-input>
          <filter-bar :value="mineSearch" :fields="mineFilterFields" @search="loadMine(1)" @reset="loadMine(1)"></filter-bar>
        </div>
      </template>

      <div class="hub-catalog">
        <div class="hub-grid">
          <article v-for="item in productList" :key="item.id" class="hub-card" @click="openDetail(item, 'mine')">
            <div class="hub-card__img">
              <el-image :src="item.url" fit="contain">
                <div slot="error" class="hub-card__img-ph"><i class="el-icon-picture-outline"></i></div>
              </el-image>
            </div>
            <div class="hub-card__body">
              <div class="hub-card__name" :title="item.name">
                {{ item.name || "-" }}
                <i v-if="isFromCatalog(item)" class="el-icon-success hub-card__verified" :title="$t('productHub.fromCatalog')"></i>
              </div>
              <div class="hub-card__sub">
                <span v-if="item.no" class="hub-card__chip">#{{ item.no }}</span>
                <span class="hub-card__chip">{{ formatPrice(item.costPrice) }}</span>
                <span v-if="item.typeName" class="hub-card__chip">{{ item.typeName }}</span>
                <span v-if="item.num" class="hub-card__chip">{{ $t("product.saleCount") }}: {{ item.num }}</span>
                <span v-for="tag in item.goodsTags" :key="tag.id" class="hub-card__chip">{{ tag.name }}</span>
                <span
                  v-if="auditPill(item)"
                  :class="['vtm-pill', auditPill(item).cls]"
                  :title="rejectReason(item)"
                  >{{ auditPill(item).label }}</span
                >
              </div>
            </div>
            <div class="hub-card__actions" @click.stop>
              <el-button size="mini" v-if="isAudit(item, null)" @click="handlerUploadImg(item)">{{
                $t("product.uploadImgs")
              }}</el-button>
              <el-button size="mini" v-if="isAudit(item, 3)" @click="handlerReUploadImg(item)">{{
                $t("product.reUploadImgs")
              }}</el-button>
              <el-button size="mini" v-if="!isAudit(item, null)" @click="handlerLookImgs(item)">{{
                $t("product.lookImgs")
              }}</el-button>
              <el-button size="mini" type="warning" v-if="isAudit(item, 2)" @click="openSimilarDialog(item)">{{
                $t("productSimilar.markSimilar")
              }}</el-button>
              <el-button size="mini" @click="openDetail(item, 'mine')">{{ $t("productHub.viewDetails") }}</el-button>
              <el-button
                v-hasPerm="['product:delete']"
                size="mini"
                type="danger"
                icon="el-icon-delete"
                @click="delProduct(item)"
              ></el-button>
            </div>
          </article>
        </div>
      </div>
    </list-page>

    <!-- ===================== 素材库（图库式浏览） ===================== -->
    <list-page
      v-else
      :loading="catalogLoading"
      :error="catalogError"
      :empty="!catalogList.length"
      :empty-text="$t('productHub.emptyCatalog')"
      :total="catalogPage.totalSize || 0"
      :page-index="catalogPage.pageIndex"
      :page-size="catalogPage.pageSize"
      :page-sizes="[12, 24, 48, 96]"
      @page-change="loadCatalog"
      @size-change="catalogSizeChange"
      @retry="loadCatalog(catalogPage.pageIndex)"
    >
      <template #head-actions>
        <el-button
          type="primary"
          size="small"
          v-hasPerm="['product:library:insert']"
          icon="el-icon-plus"
          @click="addMasterProduct"
        >
          {{ $t("common.add") }}
        </el-button>
        <el-button size="small" v-hasPerm="['product:library:insert']" icon="el-icon-upload2" @click="openImportCsv">
          {{ $t("productHub.importCsv") }}
        </el-button>
      </template>

      <template #filters>
        <div class="hub-catalog__toolbar">
          <el-input
            v-model="catalogSearch.name"
            size="small"
            clearable
            class="hub-catalog__search"
            prefix-icon="el-icon-search"
            :placeholder="$t('productHub.catalogSearch')"
            @input="debouncedCatalogSearch"
            @clear="loadCatalog(1)"
            @keyup.enter.native="loadCatalog(1)"
          ></el-input>
          <filter-bar :value="catalogSearch" :fields="catalogFilterFields" @search="loadCatalog(1)" @reset="loadCatalog(1)"></filter-bar>
          <el-select v-model="addedFilter" size="small" class="hub-catalog__added-filter">
            <el-option :label="$t('productHub.filterAddedAll')" value="all"></el-option>
            <el-option :label="$t('productHub.filterAddedOnly')" value="added"></el-option>
            <el-option :label="$t('productHub.filterNotAdded')" value="notAdded"></el-option>
          </el-select>
          <el-checkbox v-model="hideReviewed" class="hub-catalog__hide-reviewed">{{ $t("productHub.hideReviewed") }}</el-checkbox>
          <span class="hub-catalog__hint">{{ reviewedCountLabel }}</span>
          <span class="hub-catalog__hint">{{ $t("productHub.selectHint") }}</span>
        </div>
      </template>

      <div class="hub-catalog">
        <div class="hub-grid">
          <article
            v-for="item in visibleCatalogList"
            :key="item.id"
            :class="['hub-card', { 'is-selected': isSelected(item), 'is-added': isAdded(item), 'is-reviewed': isReviewed(item) }]"
            @click="openDetail(item, 'catalog')"
          >
            <label v-if="!isAdded(item)" class="hub-card__check" @click.stop>
              <el-checkbox :value="isSelected(item)" @change="toggleSelect(item)"></el-checkbox>
            </label>

            <div class="hub-card__img">
              <el-image :src="item.url" fit="contain">
                <div slot="error" class="hub-card__img-ph"><i class="el-icon-picture-outline"></i></div>
              </el-image>
              <div v-if="isAdded(item) && !isLibraryAdmin" class="hub-card__watermark">{{ $t("productHub.added") }}</div>
            </div>
            <div class="hub-card__body">
              <div class="hub-card__name" :title="item.name">{{ item.name || "-" }}</div>
              <div class="hub-card__sub">
                <span v-if="item.no" class="hub-card__chip">#{{ item.no }}</span>
                <span v-if="item.brand" class="hub-card__chip">{{ item.brand }}</span>
                <span v-if="formatWeight(item)" class="hub-card__chip">{{ formatWeight(item) }}</span>
                <span v-if="item.typeName" class="hub-card__chip">{{ item.typeName }}</span>
                <span v-if="auditPill(item)" :class="['vtm-pill', auditPill(item).cls]">{{ auditPill(item).label }}</span>
                <span v-if="isAdded(item) && !isLibraryAdmin" class="hub-card__added-inline"><i class="el-icon-check"></i> {{ $t("productHub.added") }}</span>
                <span
                  :class="['hub-card__reviewed-toggle', { 'is-on': isReviewed(item) }]"
                  @click.stop="toggleReviewed(item)"
                >
                  <i :class="isReviewed(item) ? 'el-icon-check' : 'el-icon-view'"></i>
                  {{ isReviewed(item) ? $t("productHub.reviewed") : $t("productHub.markReviewed") }}
                </span>
                <span
                  :class="['hub-card__flag-toggle', { 'is-on': item.needsReview }]"
                  :title="item.needsReview ? item.reviewNote : ''"
                  @click.stop="openFlagDialog(item)"
                >
                  <i class="el-icon-warning"></i>
                  {{ item.needsReview ? $t("productHub.flagged") : $t("productHub.flagForReview") }}
                </span>
              </div>
            </div>
            <div class="hub-card__actions" @click.stop>
              <el-button
                v-if="!isAdded(item)"
                type="primary"
                size="mini"
                :loading="obtainingId === item.id"
                @click="obtainOne(item)"
              >
                {{ $t("productHub.addToMine") }}
              </el-button>
              <el-button size="mini" @click="openDetail(item, 'catalog')">{{ $t("productHub.viewDetails") }}</el-button>
              <el-button
                v-if="canDeleteMaster(item)"
                v-hasPerm="['product:library:delete']"
                size="mini"
                type="danger"
                icon="el-icon-delete"
                @click="delMasterProduct(item)"
              ></el-button>
            </div>
          </article>
        </div>
      </div>
    </list-page>

    <!-- 批量添加吸底操作条 -->
    <transition name="hub-bulk">
      <div v-if="mode === 'catalog' && selectedIds.length" class="hub-bulkbar">
        <span class="hub-bulkbar__count">{{ selectedIds.length }}</span>
        <el-button type="primary" size="small" :loading="bulkAdding" @click="obtainSelected">
          {{ $t("productHub.addSelected", { n: selectedIds.length }) }}
        </el-button>
        <el-button type="text" size="small" class="hub-bulkbar__clear" @click="selectedIds = []">
          {{ $t("productHub.clearSelection") }}
        </el-button>
      </div>
    </transition>

    <!-- 共享详情抽屉 -->
    <el-drawer
      :visible.sync="detailVisible"
      :with-header="false"
      size="560px"
      :append-to-body="true"
      @closed="detailItem = {}"
    >
      <div class="hub-detail">
        <div class="hub-detail__topbar">
          <div class="hub-detail__title">
            <i class="el-icon-goods"></i>
            <span>{{ $t("productHub.detailTitle") }}</span>
          </div>
          <el-button type="text" icon="el-icon-close" @click="detailVisible = false"></el-button>
        </div>
        <div class="hub-detail__scroll">
          <div class="hub-detail__hero">
            <el-image class="hub-detail__img" :src="detailItem.url" fit="contain" :preview-src-list="[detailItem.url]">
              <div slot="error" class="hub-card__img-ph"><i class="el-icon-picture-outline"></i></div>
            </el-image>
            <div class="hub-detail__headline">
              <h2>
                {{ (detailOrigin === "mine" || canEditMasterDetail ? editModel.name : detailItem.name) || "-" }}
                <i
                  v-if="detailOrigin === 'mine' && isFromCatalog(detailItem)"
                  class="el-icon-success hub-card__verified"
                  :title="$t('productHub.fromCatalog')"
                ></i>
              </h2>
              <div class="hub-detail__tags">
                <span v-if="auditPill(detailItem)" :class="['vtm-pill', auditPill(detailItem).cls]">{{
                  auditPill(detailItem).label
                }}</span>
                <span v-if="detailOrigin === 'catalog' && isAdded(detailItem) && !isLibraryAdmin" class="vtm-pill vtm-pill--ok">
                  {{ $t("productHub.added") }}
                </span>
                <span v-if="detailOrigin === 'mine'" class="hub-card__chip">{{
                  $t("product.saleCount")
                }}: {{ detailItem.num || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- 我的商品：内联编辑（全部字段，无需弹窗，与素材库编辑区风格一致） -->
          <template v-if="detailOrigin === 'mine'">
            <div class="hub-edit">
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("product.goodsName") }}</span>
                  <el-input v-model="editModel.name" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldRetailPrice") }}</span>
                  <el-input v-model="editModel.costPrice" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.category") }}</span>
                  <el-select v-model="editModel.typeId" size="small" filterable clearable :placeholder="$t('common.select')">
                    <el-option v-for="t in allTypeList" :key="t.id" :label="t.name" :value="t.id"></el-option>
                  </el-select>
                </label>
                <label class="hub-field">
                  <span>
                    {{ $t("product.code") }}
                    <el-tooltip :content="$t('product.codeHint')" placement="top">
                      <i class="el-icon-info hub-field__hint"></i>
                    </el-tooltip>
                  </span>
                  <el-input v-model="editModel.no" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("addProduct.goodsBarcode") }}</span>
                  <el-input v-model="editModel.goodsBarcode" size="small"></el-input>
                </label>
                <label class="hub-field hub-field--full">
                  <span>{{ $t("productHub.tags") }}</span>
                  <el-select v-model="editModel.goodsTags" multiple collapse-tags size="small" :placeholder="$t('common.select')">
                    <el-option v-for="t in productTags" :key="t.id" :label="t.name" :value="t.id"></el-option>
                  </el-select>
                </label>
                <label class="hub-field hub-field--full">
                  <span>{{ $t("addProduct.describe") }}</span>
                  <el-input v-model="editModel.content" type="textarea" :rows="3"></el-input>
                </label>
                <div class="hub-field hub-field--full">
                  <span>{{ $t("product.image") }}</span>
                  <media-upload
                    :key="'mine-' + editModel.id"
                    :action="goodsUploadUrl"
                    accept=".jpg,.jpeg,.png,.gif"
                    v-model="editModel.url"
                  ></media-upload>
                </div>
              </div>
            </div>
          </template>

          <!-- 素材库：有权限时内联编辑（全部字段 + GNPD 扩展字段） -->
          <template v-else-if="canEditMasterDetail">
            <div class="hub-edit">
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("product.goodsName") }}</span>
                  <el-input v-model="editModel.name" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.category") }}</span>
                  <el-select v-model="editModel.typeId" size="small" filterable clearable :placeholder="$t('common.select')">
                    <el-option v-for="t in allTypeList" :key="t.id" :label="t.name" :value="t.id"></el-option>
                  </el-select>
                </label>
                <label class="hub-field">
                  <span>
                    {{ $t("product.code") }}
                    <el-tooltip :content="$t('product.codeHint')" placement="top">
                      <i class="el-icon-info hub-field__hint"></i>
                    </el-tooltip>
                  </span>
                  <el-input v-model="editModel.no" size="small" disabled></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("addProduct.goodsBarcode") }}</span>
                  <el-input v-model="editModel.goodsBarcode" size="small"></el-input>
                </label>
                <label class="hub-field hub-field--full">
                  <span>{{ $t("productHub.tags") }}</span>
                  <el-select v-model="editModel.goodsTags" multiple collapse-tags size="small" :placeholder="$t('common.select')">
                    <el-option v-for="t in productTags" :key="t.id" :label="t.name" :value="t.id"></el-option>
                  </el-select>
                </label>
                <label class="hub-field hub-field--full">
                  <span>{{ $t("addProduct.describe") }}</span>
                  <el-input v-model="editModel.content" type="textarea" :rows="3"></el-input>
                </label>
                <div class="hub-field hub-field--full">
                  <span>{{ $t("product.image") }}</span>
                  <media-upload
                    :key="'catalog-' + editModel.id"
                    :action="goodsUploadUrl"
                    accept=".jpg,.jpeg,.png,.gif"
                    v-model="editModel.url"
                  ></media-upload>
                </div>
              </div>

              <div class="hub-edit__section-title">{{ $t("productHub.sectionBrandInfo") }}</div>
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldBrand") }}</span>
                  <el-input v-model="editModel.brand" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldCompanyName") }}</span>
                  <el-input v-model="editModel.companyName" size="small"></el-input>
                </label>
              </div>

              <div class="hub-edit__section-title">{{ $t("productHub.sectionFlavor") }}</div>
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldSubcategory") }}</span>
                  <el-input v-model="editModel.subcategory" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldFlavourCategory") }}</span>
                  <el-input v-model="editModel.flavourCategory" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldFlavourSubcategory") }}</span>
                  <el-input v-model="editModel.flavourSubcategory" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldFlavours") }}</span>
                  <el-input v-model="editModel.flavours" size="small"></el-input>
                </label>
              </div>

              <div class="hub-edit__section-title">{{ $t("productHub.sectionAttributes") }}</div>
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldStorageType") }}</span>
                  <el-input v-model="editModel.storageType" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldAbv") }}</span>
                  <el-input-number
                    v-model="editModel.alcoholByVolumePercent"
                    :min="0"
                    :max="100"
                    :precision="2"
                    :controls="false"
                    size="small"
                  ></el-input-number>
                </label>
              </div>

              <div class="hub-edit__section-title">{{ $t("productHub.sectionPackaging") }}</div>
              <div class="hub-edit__grid">
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackSizeUnit") }}</span>
                  <el-select v-model="editModel.packSizeUnit" size="small" :placeholder="$t('common.select')">
                    <el-option label="g" value="g"></el-option>
                    <el-option label="fl oz" value="ml"></el-option>
                  </el-select>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldNetWeight") }}</span>
                  <el-input-number v-model="netWeightDisplay" :min="0" :precision="2" :controls="false" size="small"></el-input-number>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackageDepth") }}</span>
                  <el-input-number
                    v-model="editModel.packagePrimaryDepthMm"
                    :min="0"
                    :precision="2"
                    :controls="false"
                    size="small"
                  ></el-input-number>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackageHeight") }}</span>
                  <el-input-number
                    v-model="editModel.packagePrimaryHeightMm"
                    :min="0"
                    :precision="2"
                    :controls="false"
                    size="small"
                  ></el-input-number>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackageWidth") }}</span>
                  <el-input-number
                    v-model="editModel.packagePrimaryWidthMm"
                    :min="0"
                    :precision="2"
                    :controls="false"
                    size="small"
                  ></el-input-number>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackageMaterial") }}</span>
                  <el-input v-model="editModel.packagePrimaryMaterial" size="small"></el-input>
                </label>
                <label class="hub-field">
                  <span>{{ $t("productHub.fieldPackageType") }}</span>
                  <el-input v-model="editModel.packagePrimaryPackageType" size="small"></el-input>
                </label>
              </div>

              <div class="hub-edit__grid">
                <label class="hub-field hub-field--full">
                  <span>{{ $t("productHub.fieldSourceUrl") }}</span>
                  <el-input v-model="editModel.sourceRecordUrl" size="small"></el-input>
                </label>
              </div>
            </div>
          </template>

          <!-- 素材库：无编辑权限，只读全部字段 -->
          <div v-else class="hub-detail__kv">
            <div v-for="field in detailFields" :key="field.label" class="hub-detail__kv-item">
              <span class="hub-detail__kv-label">{{ field.label }}</span>
              <span class="hub-detail__kv-value">{{ field.value }}</span>
            </div>
          </div>

          <div class="hub-detail__section">
            <div class="hub-detail__section-head">
              <span><i class="el-icon-camera"></i> {{ $t("productHub.recognitionImages") }}</span>
              <el-tag size="mini" type="info">{{ detailImages.length }}</el-tag>
              <span
                v-if="canEditMasterDetail"
                :class="['hub-detail__reviewed-toggle', { 'is-on': isReviewed(detailItem) }]"
                @click="toggleReviewed(detailItem)"
              >
                <i :class="isReviewed(detailItem) ? 'el-icon-check' : 'el-icon-view'"></i>
                {{ isReviewed(detailItem) ? $t("productHub.reviewed") : $t("productHub.markReviewed") }}
              </span>
              <span
                v-if="canEditMasterDetail"
                :class="['hub-detail__flag-toggle', { 'is-on': detailItem.needsReview }]"
                @click="openFlagDialog(detailItem)"
              >
                <i class="el-icon-warning"></i>
                {{ detailItem.needsReview ? $t("productHub.flagged") : $t("productHub.flagForReview") }}
              </span>
            </div>
            <div v-if="canEditMasterDetail && detailItem.needsReview" class="hub-detail__flag-note">
              <i class="el-icon-warning"></i>
              <span>{{ detailItem.reviewNote }}</span>
            </div>
            <div v-if="canEditMasterDetail" class="hub-detail__vision-status">
              <span v-if="auditPill(detailItem)" :class="['vtm-pill', auditPill(detailItem).cls]">{{
                auditPill(detailItem).label
              }}</span>
              <template v-else>
                <span class="vtm-pill vtm-pill--off">{{ $t("productHub.statusNotSubmitted") }}</span>
                <el-button
                  size="mini"
                  type="primary"
                  :disabled="!detailImages.length"
                  :loading="submittingForReview"
                  @click="submitDetailForReview"
                >
                  {{ $t("productHub.submitForReview") }}
                </el-button>
              </template>
            </div>
            <div v-if="canEditMasterDetail && detailImages.length" class="hub-detail__bulk-imgs">
              <el-checkbox
                :value="allImagesSelected"
                :indeterminate="someImagesSelected"
                @change="toggleSelectAllImages"
              >
                {{ $t("productHub.selectAll") }}
              </el-checkbox>
              <el-button
                size="mini"
                type="danger"
                :disabled="!selectedImageIds.length"
                :loading="bulkDeletingImages"
                @click="deleteSelectedImages"
              >
                {{ $t("productHub.deleteSelected", { count: selectedImageIds.length }) }}
              </el-button>
            </div>
            <div v-loading="detailImagesLoading">
              <div v-if="detailImages.length || canEditMasterDetail" class="hub-detail__imgs">
                <div
                  v-for="img in detailImages"
                  :key="img.id"
                  :class="['hub-detail__img-wrap', { 'is-primary': isPrimaryImage(img) }]"
                >
                  <span v-if="isPrimaryImage(img)" class="hub-detail__img-badge">{{ $t("productHub.primaryImage") }}</span>
                  <el-image
                    class="hub-detail__img-cell"
                    :src="img.image"
                    fit="cover"
                    :preview-src-list="detailImageUrls"
                  ></el-image>
                  <div v-if="canEditMasterDetail" class="hub-detail__img-toolbar" @click.stop>
                    <el-checkbox :value="isImageSelected(img)" @change="toggleImageSelect(img)"></el-checkbox>
                    <i
                      :class="[
                        'hub-detail__img-action',
                        isPrimaryImage(img) ? 'el-icon-star-on is-set' : 'el-icon-star-off'
                      ]"
                      :title="isPrimaryImage(img) ? $t('productHub.primaryImage') : $t('productHub.setAsPrimary')"
                      @click="setAsPrimaryImage(img)"
                    ></i>
                    <i
                      class="hub-detail__img-action el-icon-delete is-danger"
                      :class="{ 'is-loading': removingImageId === img.id }"
                      :title="$t('common.delete')"
                      @click="removeRecognitionImage(img)"
                    ></i>
                  </div>
                </div>
                <media-upload
                  v-if="canEditMasterDetail"
                  :key="'add-img-' + (detailItem.id || detailItem.goodsId)"
                  class="hub-detail__img-add"
                  :action="goodsUploadUrl"
                  accept=".jpg,.jpeg,.png,.gif"
                  v-model="newRecognitionImageUrl"
                  @success="addRecognitionImage"
                ></media-upload>
              </div>
              <div v-else class="hub-detail__noimg">{{ $t("productHub.noImages") }}</div>
            </div>
          </div>
        </div>
        <div class="hub-detail__footer">
          <template v-if="detailOrigin === 'mine'">
            <el-button size="small" v-hasPerm="['product:delete']" type="danger" @click="delProduct(detailItem)">{{
              $t("common.delete")
            }}</el-button>
            <el-button
              v-if="canPromote(detailItem)"
              size="small"
              type="warning"
              :loading="promoting"
              @click="promoteToMaster(detailItem)"
              >{{ $t("productHub.promoteToMaster") }}</el-button
            >
            <el-button
              size="small"
              type="primary"
              :loading="savingDetail"
              v-hasPerm="['product:update']"
              @click="saveDetail"
              >{{ $t("productHub.saveChanges") }}</el-button
            >
          </template>
          <template v-else>
            <el-button
              v-if="!isAdded(detailItem)"
              type="primary"
              size="small"
              :loading="obtainingId === detailItem.id"
              @click="obtainOne(detailItem)"
            >
              {{ $t("productHub.addToMine") }}
            </el-button>
            <el-button v-if="canEditMasterDetail" size="small" type="primary" :loading="savingDetail" @click="saveDetail">
              {{ $t("productHub.saveChanges") }}
            </el-button>
          </template>
          <el-button size="small" @click="detailVisible = false">{{ $t("common.close") }}</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- 分类与标签管理抽屉 -->
    <el-drawer
      :visible.sync="organizeVisible"
      :title="$t('productHub.organize')"
      size="520px"
      :append-to-body="true"
    >
      <div class="hub-organize">
        <el-tabs v-model="organizeTab">
          <el-tab-pane :label="$t('productHub.categories')" name="types">
            <div class="hub-organize__toolbar">
              <el-button type="primary" size="small" v-hasPerm="['product:type:insert']" icon="el-icon-plus" @click="addType">
                {{ $t("common.add") }}
              </el-button>
            </div>
            <el-table :data="typeList" v-loading="typeLoading" size="small" max-height="480">
              <el-table-column prop="name" :label="$t('productType.name')"></el-table-column>
              <el-table-column prop="notes" :label="$t('productType.notes')" show-overflow-tooltip></el-table-column>
              <el-table-column :label="$t('common.operate')" width="140" align="center">
                <template slot-scope="scope">
                  <template v-if="canManageShared(scope.row)">
                    <el-button size="mini" type="text" v-hasPerm="['product:type:update']" @click="editType(scope.row)">{{
                      $t("common.edit")
                    }}</el-button>
                    <el-button
                      size="mini"
                      type="text"
                      class="hub-danger"
                      v-hasPerm="['product:type:update']"
                      :loading="deletingTypeId === scope.row.id"
                      @click="delType(scope.row)"
                      >{{ $t("common.delete") }}</el-button
                    >
                  </template>
                  <span v-else class="hub-card__chip">{{ $t("productHub.sharedTag") }}</span>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              small
              layout="prev, pager, next"
              :current-page="typePage.pageIndex"
              :total="typePage.totalSize"
              :page-size="typePage.pageSize"
              @current-change="loadTypes"
            ></el-pagination>
          </el-tab-pane>
          <el-tab-pane :label="$t('productHub.tags')" name="tags">
            <div class="hub-organize__toolbar">
              <el-button type="primary" size="small" v-hasPerm="['product:tag:insert']" icon="el-icon-plus" @click="addTag">
                {{ $t("common.add") }}
              </el-button>
            </div>
            <el-table :data="tagList" v-loading="tagLoading" size="small" max-height="480">
              <el-table-column prop="name" :label="$t('productTag.name')"></el-table-column>
              <el-table-column prop="notes" :label="$t('productTag.notes')" show-overflow-tooltip></el-table-column>
              <el-table-column :label="$t('common.operate')" width="120" align="center">
                <template slot-scope="scope">
                  <template v-if="canManageShared(scope.row)">
                    <el-button size="mini" type="text" v-hasPerm="['product:tag:update']" @click="editTag(scope.row)">{{
                      $t("common.edit")
                    }}</el-button>
                    <el-button size="mini" type="text" class="hub-danger" v-hasPerm="['product:tag:delete']" @click="delTag(scope.row)">{{
                      $t("common.delete")
                    }}</el-button>
                  </template>
                  <span v-else class="hub-card__chip">{{ $t("productHub.sharedTag") }}</span>
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              small
              layout="prev, pager, next"
              :current-page="tagPage.pageIndex"
              :total="tagPage.totalSize"
              :page-size="tagPage.pageSize"
              @current-change="loadTags"
            ></el-pagination>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>

    <!-- GNPD CSV 批量导入素材库 -->
    <el-dialog
      :visible.sync="importCsvVisible"
      :title="$t('productHub.importDialogTitle')"
      width="640px"
      :close-on-click-modal="false"
      :append-to-body="true"
      @close="closeImportCsv"
    >
      <div class="hub-import">
        <el-upload
          action=""
          accept=".csv"
          :auto-upload="false"
          :limit="1"
          :disabled="importCsvUploading || importPolling"
          :on-change="handleImportFileChange"
          :on-remove="() => (importCsvFile = null)"
          :file-list="importCsvFile ? [importCsvFile] : []"
        >
          <el-button size="small" icon="el-icon-document">{{ $t("productHub.chooseCsvFile") }}</el-button>
        </el-upload>

        <div v-if="importResult" class="hub-import__progress">
          <el-progress
            :percentage="importProgressPercent"
            :status="importResult.done ? 'success' : undefined"
          ></el-progress>
          <div class="hub-import__counts">
            {{
              $t("productHub.importProgress", {
                done: (importResult.successCount || 0) + (importResult.skippedCount || 0) + (importResult.failedCount || 0),
                total: importResult.totalRows || 0,
                success: importResult.successCount || 0,
                skipped: importResult.skippedCount || 0,
                failed: importResult.failedCount || 0
              })
            }}
          </div>
          <el-table
            v-if="importFailedRows.length"
            :data="importFailedRows"
            size="mini"
            max-height="240"
            class="hub-import__failed-table"
          >
            <el-table-column prop="rowNum" :label="$t('productHub.importRowNum')" width="70"></el-table-column>
            <el-table-column prop="productName" :label="$t('productHub.importRowName')" min-width="140"></el-table-column>
            <el-table-column prop="status" :label="$t('productHub.importRowStatus')" width="90"></el-table-column>
            <el-table-column prop="message" :label="$t('productHub.importRowMessage')" min-width="200"></el-table-column>
          </el-table>
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="importCsvVisible = false">{{ $t("common.close") }}</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="!importCsvFile"
          :loading="importCsvUploading"
          @click="startImportCsv"
        >
          {{ $t("productHub.startImport") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 标记需要管理员复核 -->
    <el-dialog
      :visible.sync="flagDialogVisible"
      :title="$t('productHub.flagForReview')"
      width="480px"
      :append-to-body="true"
    >
      <el-input
        v-model="flagNoteDraft"
        type="textarea"
        :rows="4"
        :placeholder="$t('productHub.flagNotePlaceholder')"
      ></el-input>
      <span slot="footer">
        <el-button v-if="flagDialogItem && flagDialogItem.needsReview" size="small" @click="clearFlag">
          {{ $t("productHub.clearFlag") }}
        </el-button>
        <el-button size="small" @click="flagDialogVisible = false">{{ $t("common.cancel") }}</el-button>
        <el-button size="small" type="primary" :loading="savingFlag" @click="saveFlag">
          {{ $t("productHub.saveFlag") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  // 商品中心：我的商品（管理表格）+ 素材库（图库浏览、单个/批量添加、已添加标识）
  // 整合原 Product / ProductLibrary / ProductType / ProductTag 四个页面的能力，
  // 端点与弹窗全部复用，不改动后端契约。
  import addProduct from "_c/product/AddProduct";
  import ProductFinderDialog from "_c/product/ProductFinderDialog.vue";
  import addProductType from "_c/product/AddProductType";
  import addProductTag from "_c/product/AddProductTag";
  import UploadImgs from "_c/product/UploadImgs.vue";
  import LookImgs from "_c/product/LookImgs.vue";
  import ProductSimilarDialog from "_c/product/ProductSimilarDialog.vue";
  import ListPage from "_c/common/ListPage.vue";
  import FilterBar from "_c/common/FilterBar.vue";
  import MediaUpload from "_c/common/MediaUpload.vue";
  import config from "@/http/config";
  import debounce from "@/utils/debounce";

  // packSizeUnit 内部仍存 "g" / "ml"（对应 GNPD 原始数据），仅 "ml" 需要换算展示为美制 fl oz；
  // "g" 的重量不做任何换算，直接展示/编辑原始值
  const ML_PER_FL_OZ = 29.5735;

  // GNPD（Global New Products Database）导入字段 —— 素材库商品的扩展元数据
  const GNPD_FIELDS = [
    "brand",
    "companyName",
    "datePublished",
    "sourceRecordUrl",
    "subcategory",
    "flavourCategory",
    "flavourSubcategory",
    "flavours",
    "storageType",
    "launchType",
    "alcoholByVolumePercent",
    "packSizeUnit",
    "packUnitsCount",
    "packSizeTotal",
    "netWeight",
    "packagePrimaryDepthMm",
    "packagePrimaryHeightMm",
    "packagePrimaryWidthMm",
    "packagePrimaryMaterial",
    "packagePrimaryPackageType"
  ];

  const createEmptyProduct = () => ({
    costPrice: "",
    goodsTags: [],
    name: "",
    typeId: "",
    url: "",
    content: "",
    introduction: "",
    no: "",
    goodsBarcode: "",
    notes: "",
    id: "",
    ...GNPD_FIELDS.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {})
  });

  export default {
    name: "ProductHub",
    components: {
      addProduct,
      ProductFinderDialog,
      addProductType,
      addProductTag,
      UploadImgs,
      LookImgs,
      ProductSimilarDialog,
      ListPage,
      FilterBar,
      MediaUpload
    },
    data() {
      return {
        showSimilarDialog: false,
        similarProduct: {},
        mode: "mine",
        // ---- 我的商品 ----
        mineSearch: { name: "", typeName: "", no: "" },
        mineLoading: false,
        mineError: false,
        minePage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        productList: [],
        // ---- 素材库 ----
        catalogSearch: { name: "", typeId: "", skuResult: "", needsReview: "", tagId: "" },
        catalogLoading: false,
        catalogError: false,
        catalogPage: { pageSize: 24, pageIndex: 1, totalSize: 0 },
        catalogList: [],
        selectedIds: [],
        obtainingId: "",
        promoting: false,
        bulkAdding: false,
        obtainedIds: [], // 本次会话内已添加的素材 id（“已添加”回退方案之一）
        // 人工审核进度标记（临时功能：落库 goods.manual_reviewed，供审核人跟踪已看过哪些素材库商品）
        togglingReviewedId: "",
        hideReviewed: false,
        addedFilter: "all", // all | added | notAdded —— 是否已加入我的商品（纯前端过滤，仅作用于当前页）
        ownedSourceIds: [], // 我的商品 sourceGoodsId 索引（“已添加”精确匹配）
        ownedNames: [], // 我的商品名称索引（“已添加”启发式匹配，旧数据兜底）
        finderVisible: false, // 「先在素材库中查找」入口（引导优先复用主库）
        pId: -1,
        uId: -1,
        // ---- 共享弹窗 ----
        dialogProduct: false,
        fromMaterial: "0",
        product: createEmptyProduct(),
        productTags: [],
        showUploadImg: false,
        showLookImg: false,
        productItem: {},
        isReUpload: false,
        // ---- 详情抽屉 ----
        detailVisible: false,
        detailItem: {},
        detailOrigin: "mine",
        detailImages: [],
        detailImagesLoading: false,
        newRecognitionImageUrl: "",
        removingImageId: null,
        selectedImageIds: [],
        bulkDeletingImages: false,
        // ---- 标记需要管理员复核（供审核人向管理员提问题） ----
        flagDialogVisible: false,
        flagDialogItem: null,
        flagNoteDraft: "",
        savingFlag: false,
        submittingForReview: false,
        // ---- CSV 批量导入 ----
        importCsvVisible: false,
        importCsvFile: null,
        importCsvUploading: false,
        importPolling: false,
        importBatchId: "",
        importResult: null,
        importPollTimer: null,
        // 我的商品：抽屉内联编辑
        editModel: createEmptyProduct(),
        savingDetail: false,
        // ---- 分类与标签 ----
        organizeVisible: false,
        organizeTab: "types",
        typeList: [],
        typeLoading: false,
        typePage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        dialogProductType: false,
        productType: { id: "", name: "", notes: "" },
        deletingTypeId: "",
        allTypeList: [],
        tagList: [],
        tagLoading: false,
        tagPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        dialogProductTag: false,
        productTag: { id: "", name: "", notes: "" }
      };
    },
    computed: {
      mineFilterFields() {
        return [
          {
            key: "typeName",
            type: "select",
            placeholder: this.$t("productHub.filterCategory"),
            options: (this.allTypeList || []).map(item => ({ label: item.name, value: item.name })),
            width: "170px"
          }
        ];
      },
      catalogFilterFields() {
        return [
          {
            key: "typeId",
            type: "select",
            placeholder: this.$t("productHub.filterCategory"),
            options: [
              { label: this.$t("productHub.filterBlank"), value: "__BLANK__" },
              ...(this.allTypeList || []).map(item => ({ label: item.name, value: item.id }))
            ],
            width: "170px"
          },
          {
            key: "skuResult",
            type: "select",
            placeholder: this.$t("productHub.filterAudit"),
            options: [
              { label: this.$t("productHub.statusPending"), value: 0 },
              { label: this.$t("productHub.statusAuditing"), value: 1 },
              { label: this.$t("productHub.statusApproved"), value: 2 },
              { label: this.$t("productHub.statusRejected"), value: 3 }
            ],
            width: "170px"
          },
          {
            key: "tagId",
            type: "select",
            placeholder: this.$t("productHub.filterTag"),
            options: (this.productTags || []).map(item => ({ label: item.name, value: item.id })),
            width: "160px"
          },
          {
            key: "needsReview",
            type: "select",
            placeholder: this.$t("productHub.filterFlagged"),
            options: [{ label: this.$t("productHub.flagged"), value: 1 }],
            width: "160px"
          }
        ];
      },
      // 当前页内按“已审核”“是否已添加”过滤（纯前端，不涉及后端分页/总数）
      visibleCatalogList() {
        let list = this.catalogList;
        if (this.hideReviewed) {
          list = list.filter(item => !this.isReviewed(item));
        }
        if (this.addedFilter === "added") {
          list = list.filter(item => this.isAdded(item));
        } else if (this.addedFilter === "notAdded") {
          list = list.filter(item => !this.isAdded(item));
        }
        return list;
      },
      reviewedCountLabel() {
        const reviewedOnPage = this.catalogList.filter(item => this.isReviewed(item)).length;
        return this.$t("productHub.reviewedCount", { reviewed: reviewedOnPage, total: this.catalogList.length });
      },
      goodsUploadUrl() {
        return config.baseUrl + "upload/goods";
      },
      canEditMasterDetail() {
        return (
          this.detailOrigin === "catalog" &&
          this.hasPermission("product:library:update") &&
          this.canEditMaster(this.detailItem || {})
        );
      },
      // "Added to my items" banners only matter for operators deciding what to
      // add next — an admin browsing the catalog is doing curation/reconciliation,
      // not personal shopping, so the badge is just noise for them.
      isLibraryAdmin() {
        return this.hasPermission("product:library:update");
      },
      detailImageUrls() {
        return this.detailImages.map(img => img.image);
      },
      allImagesSelected() {
        return !!this.detailImages.length && this.selectedImageIds.length === this.detailImages.length;
      },
      someImagesSelected() {
        return this.selectedImageIds.length > 0 && !this.allImagesSelected;
      },
      importProgressPercent() {
        if (!this.importResult || !this.importResult.totalRows) {
          return 0;
        }
        const done = (this.importResult.successCount || 0) + (this.importResult.skippedCount || 0) + (this.importResult.failedCount || 0);
        return Math.min(100, Math.round((done / this.importResult.totalRows) * 100));
      },
      importFailedRows() {
        if (!this.importResult || !Array.isArray(this.importResult.details)) {
          return [];
        }
        return this.importResult.details.filter(row => row && row.status && row.status !== "success");
      },
      // packSizeUnit === "ml" 时按 fl oz 展示/编辑（换算），其余（如 "g"）直接展示原始值，不做任何换算
      netWeightDisplay: {
        get() {
          const raw = Number(this.editModel.netWeight);
          if (!raw) {
            return null;
          }
          if (this.editModel.packSizeUnit === "ml") {
            return Math.round((raw / ML_PER_FL_OZ) * 100) / 100;
          }
          return raw;
        },
        set(value) {
          if (value === null || value === "") {
            this.editModel.netWeight = "";
            return;
          }
          this.editModel.netWeight =
            this.editModel.packSizeUnit === "ml" ? Math.round(Number(value) * ML_PER_FL_OZ * 100) / 100 : Number(value);
        }
      },
      // 只读详情（素材库）——展示商品的全部可用字段
      detailFields() {
        const item = this.detailItem || {};
        const stripHtml = html => String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        const fields = [
          { label: this.$t("product.goodsName"), value: item.name || "-" },
          { label: this.$t("product.code"), value: item.no || "-" },
          { label: this.$t("addProduct.goodsBarcode"), value: item.goodsBarcode || "-" },
          { label: this.$t("productHub.category"), value: item.typeName || "-" },
          {
            label: this.$t("productHub.tags"),
            value: (item.goodsTags || []).map(tag => tag.name).join(", ") || "-"
          },
          { label: this.$t("addProduct.describe"), value: item.content || "-" }
        ];
        if (item.introduction) {
          fields.push({ label: this.$t("addProduct.introduction"), value: stripHtml(item.introduction) || "-" });
        }
        if (item.num !== undefined && item.num !== null) {
          fields.push({ label: this.$t("product.saleCount"), value: item.num });
        }
        if (item.createTime) {
          fields.push({ label: this.$t("product.createTime"), value: item.createTime });
        }
        const reject = this.rejectReason(item);
        if (reject) {
          fields.push({ label: this.$t("product.rejectReason"), value: reject });
        }
        return fields;
      }
    },
    async mounted() {
      this.pId = localStorage.getItem("pId") || -1;
      this.uId = localStorage.getItem("uId") || -1;
      if (this.$route.query.tab === "catalog") {
        this.mode = "catalog";
      }
      this.debouncedCatalogSearch = debounce(() => this.loadCatalog(1), 350);
      this.debouncedMineSearch = debounce(() => this.loadMine(1), 350);
      this.loadMine();
      this.loadCatalog();
      this.loadOwnedIndex();
      this.listProductTag();
      this.loadAllTypes();
      this.loadTypes();
      this.loadTags();
    },
    methods: {
      // ================= 我的商品 =================
      mineSizeChange(pageSize) {
        this.minePage.pageSize = pageSize;
        this.loadMine();
      },
      loadMine(pageIndex) {
        this.mineLoading = true;
        this.mineError = false;
        this.minePage.pageIndex = pageIndex ? pageIndex : 1;
        const obj = this.$cloud.objMerge(this.mineSearch, this.minePage);
        this.$api
          .request(this.$url.Product.page, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              const records = Array.isArray(res.data.records) ? res.data.records : [];
              this.productList = records.map(this.normalizeMineProduct);
              this.minePage.totalSize = parseInt(res.data.total) || 0;
            }
            this.mineLoading = false;
          })
          .catch(() => {
            this.mineLoading = false;
            this.mineError = true;
          });
      },
      sortChange(column) {
        if (column.order === "ascending") {
          this.mineSearch.order = "asc";
          this.mineSearch.sort = column.prop;
        } else if (column.order === "descending") {
          this.mineSearch.order = "desc";
          this.mineSearch.sort = column.prop;
        } else {
          delete this.mineSearch.sort;
          delete this.mineSearch.order;
        }
        this.loadMine(1);
      },
      // 「新增」先进入主库查找，引导复用主产品，避免重复创建
      addProduct() {
        this.finderVisible = true;
      },
      // 主库里确实没有 → 才打开空白新建表单（保留原新建能力）
      createNewProduct(prefillName) {
        this.fromMaterial = "0";
        this.product = createEmptyProduct();
        if (prefillName) {
          this.product.name = prefillName;
        }
        this.dialogProduct = true;
      },
      onFinderObtained() {
        this.loadOwnedIndex();
        this.loadMine(this.minePage.pageIndex);
      },
      // 从新建表单中选择「改用主库产品」→ 领用并刷新
      onUseMaster(item) {
        if (!item || !item.id) {
          return;
        }
        this.obtain(item.id)
          .then(res => {
            if (res && res.code === 0) {
              this.$message.success(this.$t("productHub.addedToMine", { name: item.name }));
              this.loadOwnedIndex();
              this.loadMine(this.minePage.pageIndex);
            } else {
              this.$message.warning((res && res.msg) || this.$t("common.loadFailed"));
            }
          })
          .catch(() => this.$message.error(this.$t("common.loadFailed")));
      },
      editProduct(row) {
        this.fromMaterial = "0";
        this.detailVisible = false;
        const next = createEmptyProduct();
        Object.keys(next).forEach(key => {
          next[key] = row[key];
        });
        this.product = next;
        this.dialogProduct = true;
      },
      delProduct(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api
            .request(this.$url.Product.delete, this.$method.delete, { id: row.id })
            .then(res => {
              if (res.code === 0) {
                this.$message.success(res.msg);
                this.detailVisible = false;
                this.loadMine(this.minePage.pageIndex);
                this.loadOwnedIndex();
              }
            })
            .catch(() => {});
        });
      },
      // Creates an independent copy in the Master Library; the operator's own
      // product (`row`) is only ever read on the backend, never modified.
      promoteToMaster(row) {
        this.$confirm(this.$t("productHub.promoteConfirm"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.promoting = true;
            this.$api
              .request(this.$url.ProductLibrary.promote, this.$method.post, { id: row.id })
              .then(res => {
                this.promoting = false;
                if (res.code === 0) {
                  this.$message.success(this.$t("productHub.promoteSuccess"));
                  this.loadCatalog(1);
                } else {
                  this.$message.error(res.msg || this.$t("common.loadFailed"));
                }
              })
              .catch(err => {
                this.promoting = false;
                this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
              });
          })
          .catch(() => {});
      },
      // 后端只接受已通过视觉审核、且不是从素材库领用来的商品
      canPromote(item) {
        return this.isLibraryAdmin && !!item && !this.isFromCatalog(item) && this.isAudit(item, 2);
      },
      cancelProductDialog(dialog, state) {
        this.dialogProduct = dialog;
        if (state) {
          if (this.fromMaterial === "1") {
            this.loadCatalog(this.catalogPage.pageIndex);
          } else {
            this.loadMine(this.minePage.pageIndex);
            this.loadOwnedIndex();
          }
        }
      },
      // ---- 识别图（两个库共用） ----
      handlerUploadImg(row) {
        this.productItem = row;
        this.isReUpload = false;
        this.showUploadImg = true;
      },
      handlerReUploadImg(row) {
        this.productItem = row;
        this.isReUpload = true;
        this.showUploadImg = true;
      },
      closeUploadImg() {
        this.showUploadImg = false;
      },
      refreshUploadImg() {
        this.showUploadImg = false;
        this.loadMine(this.minePage.pageIndex);
        this.loadCatalog(this.catalogPage.pageIndex);
      },
      handlerLookImgs(row) {
        this.productItem = row;
        this.showLookImg = true;
      },
      closeLookImg() {
        this.showLookImg = false;
      },
      openSimilarDialog(row) {
        this.similarProduct = row;
        this.showSimilarDialog = true;
      },
      closeSimilarDialog() {
        this.showSimilarDialog = false;
      },
      refreshSimilarProduct() {
        this.loadMine(this.minePage.pageIndex);
      },
      // ================= 素材库 =================
      catalogSizeChange(pageSize) {
        this.catalogPage.pageSize = pageSize;
        this.loadCatalog();
      },
      loadCatalog(pageIndex) {
        this.catalogLoading = true;
        this.catalogError = false;
        this.catalogPage.pageIndex = pageIndex ? pageIndex : 1;
        const obj = this.$cloud.objMerge(
          {
            name: this.catalogSearch.name || "",
            typeId: this.catalogSearch.typeId || "",
            skuResult: this.catalogSearch.skuResult !== "" && this.catalogSearch.skuResult !== undefined ? this.catalogSearch.skuResult : "",
            tagId: this.catalogSearch.tagId || "",
            needsReview: this.catalogSearch.needsReview !== "" && this.catalogSearch.needsReview !== undefined ? this.catalogSearch.needsReview : ""
          },
          this.catalogPage
        );
        this.$api
          .request(this.$url.ProductLibrary.page, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              const records = Array.isArray(res.data.records) ? res.data.records : [];
              this.catalogList = records.map(this.normalizeOpenDoorSku);
              this.catalogPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.catalogLoading = false;
          })
          .catch(() => {
            this.catalogLoading = false;
            this.catalogError = true;
          });
      },
      // 我的商品：后端在不同接口里字段名不一致（name/goodsName、url/goodsUrl、
      // costPrice/goodsPrice、typeName/goodsTypeName），未归一会导致表格出现空白列。
      normalizeMineProduct(row) {
        if (!row || typeof row !== "object") {
          return row;
        }
        const pick = (...vals) => {
          for (let i = 0; i < vals.length; i++) {
            const v = vals[i];
            if (v !== undefined && v !== null && v !== "") {
              return v;
            }
          }
          return "";
        };
        return {
          ...row,
          name: pick(row.name, row.goodsName),
          url: pick(row.url, row.goodsUrl, row.imgUrl),
          costPrice: pick(row.costPrice, row.goodsPrice, row.price),
          typeName: pick(row.typeName, row.goodsTypeName, row.type && row.type.name),
          no: pick(row.no, row.goodsNo, row.code),
          goodsBarcode: pick(row.goodsBarcode, row.barcode)
        };
      },
      normalizeOpenDoorSku(row) {
        const goodsId = row.goodsId || row.id || "";
        const skuInfo = row.goodsOpenDoorSkuDTO || {
          id: row.id || "",
          goodsId,
          gpSkuId: row.gpSkuId || "",
          skuId: row.skuId || "",
          skuResult: row.skuResult,
          comments: row.comments || "",
          rejectReasion: row.rejectReasion || ""
        };
        return {
          ...row,
          goodsId,
          name: row.name || row.goodsName || "",
          url: row.url || row.goodsUrl || "",
          costPrice: row.costPrice !== undefined && row.costPrice !== null ? row.costPrice : row.goodsPrice || "",
          goodsOpenDoorSkuDTO: skuInfo
        };
      },
      // “已添加”索引：加载我的商品名称集合（启发式回退，后端加 added 标记后可替换）
      loadOwnedIndex() {
        this.$api
          .request(this.$url.Product.page, this.$method.get, { pageIndex: 1, pageSize: 500 })
          .then(res => {
            if (res.code === 0 && res.data && Array.isArray(res.data.records)) {
              this.ownedSourceIds = res.data.records.map(item => item.sourceGoodsId).filter(Boolean);
              // 名称启发式仅作为旧数据（未记录 sourceGoodsId 时）的兜底，新复制的商品应始终能通过 sourceGoodsId 精确匹配
              this.ownedNames = res.data.records.map(item => String(item.name || "").trim().toLowerCase()).filter(Boolean);
            }
          })
          .catch(() => {});
      },
      isAdded(item) {
        if (!item) {
          return false;
        }
        if (item.added === true || item.obtained === true) {
          return true; // 后端标记（未来）
        }
        if (this.obtainedIds.indexOf(item.id) !== -1) {
          return true; // 本次会话已添加
        }
        if (this.ownedSourceIds.indexOf(item.id) !== -1) {
          return true; // 精确匹配：已复制商品记录了来源商品 ID
        }
        const name = String(item.name || "").trim().toLowerCase();
        return !!name && this.ownedNames.indexOf(name) !== -1; // 名称启发式（旧数据兜底）
      },
      // "我的商品"中：该商品是否是从素材库领用（obtain）而来（sourceGoodsId 记录了来源商品 ID）
      isFromCatalog(item) {
        // isSource（0=新建 1=素材库提取）从领用逻辑一开始就有记录，覆盖全部历史数据；
        // sourceGoodsId 只是后来才加的溯源字段，早期领用的商品不会有值，不能作为唯一判断依据
        return !!(item && (item.isSource === 1 || item.sourceGoodsId));
      },
      // ---- 人工审核进度标记（临时功能：落库 goods.manual_reviewed，仅测试库） ----
      isReviewed(item) {
        return !!item && !!item.manualReviewed;
      },
      toggleReviewed(item) {
        if (!item || !item.id || this.togglingReviewedId === item.id) {
          return;
        }
        const next = item.manualReviewed ? 0 : 1;
        this.togglingReviewedId = item.id;
        this.$api
          .request(this.$url.ProductLibrary.setReviewed, this.$method.put, { id: item.id, manualReviewed: next })
          .then(res => {
            this.togglingReviewedId = "";
            if (res.code === 0) {
              this.$set(item, "manualReviewed", next);
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.togglingReviewedId = "";
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      // ---- 标记需要管理员复核（供审核人向管理员提问题） ----
      openFlagDialog(item) {
        if (!item) {
          return;
        }
        this.flagDialogItem = item;
        this.flagNoteDraft = item.reviewNote || "";
        this.flagDialogVisible = true;
      },
      async saveFlag() {
        const item = this.flagDialogItem;
        if (!item || !item.id || this.savingFlag) {
          return;
        }
        if (!this.flagNoteDraft || !this.flagNoteDraft.trim()) {
          this.$message.warning(this.$t("productHub.flagNoteRequired"));
          return;
        }
        this.savingFlag = true;
        try {
          const res = await this.$api.request(this.$url.ProductLibrary.setFlag, this.$method.put, {
            id: item.id,
            needsReview: 1,
            reviewNote: this.flagNoteDraft
          });
          if (res.code === 0) {
            this.$set(item, "needsReview", 1);
            this.$set(item, "reviewNote", this.flagNoteDraft);
            this.flagDialogVisible = false;
            this.$message.success(this.$t("productHub.flagSaved"));
          } else {
            this.$message.error(res.msg || this.$t("common.loadFailed"));
          }
        } catch (err) {
          this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
        } finally {
          this.savingFlag = false;
        }
      },
      async clearFlag() {
        const item = this.flagDialogItem;
        if (!item || !item.id || this.savingFlag) {
          return;
        }
        this.savingFlag = true;
        try {
          const res = await this.$api.request(this.$url.ProductLibrary.setFlag, this.$method.put, {
            id: item.id,
            needsReview: 0,
            reviewNote: ""
          });
          if (res.code === 0) {
            this.$set(item, "needsReview", 0);
            this.$set(item, "reviewNote", "");
            this.flagDialogVisible = false;
            this.$message.success(this.$t("productHub.flagCleared"));
          } else {
            this.$message.error(res.msg || this.$t("common.loadFailed"));
          }
        } catch (err) {
          this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
        } finally {
          this.savingFlag = false;
        }
      },
      isSelected(item) {
        return this.selectedIds.indexOf(item.id) !== -1;
      },
      toggleSelect(item) {
        const index = this.selectedIds.indexOf(item.id);
        if (index === -1) {
          this.selectedIds.push(item.id);
        } else {
          this.selectedIds.splice(index, 1);
        }
      },
      obtain(id) {
        return this.$api.request(this.$url.ProductLibrary.obtain, this.$method.post, { id });
      },
      obtainOne(item) {
        this.obtainingId = item.id;
        this.obtain(item.id)
          .then(res => {
            this.obtainingId = "";
            if (res.code === 0) {
              this.obtainedIds.push(item.id);
              this.selectedIds = this.selectedIds.filter(id => id !== item.id);
              this.$message.success(this.$t("productHub.bulkResult", { ok: 1, total: 1 }));
              this.loadMine(this.minePage.pageIndex);
              this.loadOwnedIndex();
            } else {
              this.$message.error(res.msg);
            }
          })
          .catch(err => {
            this.obtainingId = "";
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      async obtainSelected() {
        const ids = [...this.selectedIds];
        if (!ids.length || this.bulkAdding) {
          return;
        }
        this.bulkAdding = true;
        let ok = 0;
        let fail = 0;
        // 无批量端点：逐个调用（后续可换后端 batch-obtain）
        for (const id of ids) {
          try {
            const res = await this.obtain(id);
            if (res.code === 0) {
              ok += 1;
              this.obtainedIds.push(id);
            } else {
              fail += 1;
            }
          } catch (e) {
            fail += 1;
          }
        }
        this.bulkAdding = false;
        this.selectedIds = [];
        let msg = this.$t("productHub.bulkResult", { ok, total: ids.length });
        if (fail > 0) {
          msg += " · " + this.$t("productHub.bulkPartial", { fail });
        }
        this.$message[fail ? "warning" : "success"](msg);
        this.loadMine(this.minePage.pageIndex);
        this.loadOwnedIndex();
        this.loadCatalog(this.catalogPage.pageIndex);
      },
      addMasterProduct() {
        this.fromMaterial = "1";
        this.product = createEmptyProduct();
        this.dialogProduct = true;
      },
      canDeleteMaster(item) {
        return String(item.uId) === String(this.uId) || this.hasPermission("product:library:delete");
      },
      canEditMaster(item) {
        return String(item.uId) === String(this.uId) || this.hasPermission("product:library:update");
      },
      hasPermission(code) {
        const permissions = this.$store.state.permissions || [];
        return permissions.some(permission => permission === "*:*:*" || permission === code);
      },
      delMasterProduct(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api
            .request(this.$url.ProductLibrary.delete, this.$method.delete, { id: row.id })
            .then(res => {
              if (res.code === 0) {
                this.$message.success(res.msg);
                this.detailVisible = false;
                this.loadCatalog(this.catalogPage.pageIndex);
              } else {
                this.$message.error(res.msg);
              }
            })
            .catch(err => {
              this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
            });
        });
      },
      // ================= 详情抽屉 =================
      openDetail(item, origin) {
        this.detailItem = item;
        this.detailOrigin = origin;
        if (origin === "mine" || this.canEditMaster(item)) {
          this.buildEditModel(item);
        }
        this.detailVisible = true;
        this.loadDetailImages(item);
      },
      // 用当前商品初始化内联编辑模型（标签对象 → 标签ID 数组，供多选回显）
      buildEditModel(item) {
        const m = createEmptyProduct();
        ["name", "costPrice", "typeId", "no", "goodsBarcode", "content", "url", "introduction", "notes", "id", ...GNPD_FIELDS].forEach(
          key => {
            m[key] = item[key] !== undefined && item[key] !== null ? item[key] : m[key];
          }
        );
        m.goodsTags = (item.goodsTags || []).map(tag => (tag.goodsTagId !== undefined ? tag.goodsTagId : tag.id));
        if (!m.packSizeUnit) {
          m.packSizeUnit = "ml"; // 未设置时默认按 fl oz 展示/编辑
        }
        this.editModel = m;
      },
      // 内联保存（"我的商品" 走 Product.update，素材库走 ProductLibrary.update；标签ID → [{goodsTagId}]）
      saveDetail() {
        if (!this.editModel.name || !String(this.editModel.name).trim()) {
          this.$message.warning(this.$t("addProduct.pProductName"));
          return;
        }
        const isCatalog = this.detailOrigin === "catalog";
        const url = isCatalog ? this.$url.ProductLibrary.update : this.$url.Product.update;
        this.savingDetail = true;
        const payload = { ...this.editModel };
        payload.goodsTags = (this.editModel.goodsTags || []).map(id => ({ goodsTagId: id }));
        this.$api
          .request(url, this.$method.put, payload)
          .then(res => {
            this.savingDetail = false;
            if (res.code === 0) {
              this.$message.success(res.msg || this.$t("editUserMachine.up"));
              // 就地更新详情展示 + 刷新列表
              ["name", "costPrice", "typeId", "no", "goodsBarcode", "content", "url", "introduction", ...GNPD_FIELDS].forEach(key => {
                this.$set(this.detailItem, key, this.editModel[key]);
              });
              if (isCatalog) {
                this.loadCatalog(this.catalogPage.pageIndex);
              } else {
                this.loadMine(this.minePage.pageIndex);
              }
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.savingDetail = false;
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      loadDetailImages(item) {
        this.detailImages = [];
        this.selectedImageIds = [];
        const goodsId = item && (item.goodsId || item.id);
        if (!goodsId) {
          return;
        }
        this.detailImagesLoading = true;
        this.$api
          // 识别图片上限为 30 张（后端 material/import、saveOrEdit 均以此为限），
          // pageSize 给一个明显更大的值以确保一次性拿到全部图片，而不是被默认分页（每页 10 条）截断
          .request(this.$url.OpenDoor.getSkuImageList, this.$method.get, { goodsId, pageSize: 100 })
          .then(res => {
            if (res.code === 0) {
              const records = Array.isArray(res.data && res.data.records) ? res.data.records : [];
              this.detailImages = records.filter(record => record && record.image);
            }
            this.detailImagesLoading = false;
          })
          .catch(() => {
            this.detailImagesLoading = false;
          });
      },
      addRecognitionImage(payload) {
        const goodsId = this.detailItem && (this.detailItem.goodsId || this.detailItem.id);
        if (!goodsId) {
          this.newRecognitionImageUrl = "";
          return;
        }
        this.$api
          .request(this.$url.OpenDoor.materialImageInsert, this.$method.post, {
            goodsId,
            image: payload.url
          })
          .then(res => {
            if (res.code === 0) {
              this.detailImages.push(res.data);
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          })
          .finally(() => {
            this.newRecognitionImageUrl = "";
          });
      },
      removeRecognitionImage(img) {
        this.$confirm(this.$t("common.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.removingImageId = img.id;
            this.$api
              .request(this.$url.OpenDoor.materialImageDelete, this.$method.delete, { id: img.id })
              .then(res => {
                this.removingImageId = null;
                if (res.code === 0) {
                  this.detailImages = this.detailImages.filter(i => i.id !== img.id);
                } else {
                  this.$message.error(res.msg || this.$t("common.loadFailed"));
                }
              })
              .catch(err => {
                this.removingImageId = null;
                this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
              });
          })
          .catch(() => {});
      },
      isPrimaryImage(img) {
        return !!img && !!this.editModel.url && img.image === this.editModel.url;
      },
      // 仅更新表单里的 url 字段，和其它字段一样需要点“保存修改”才真正落库
      setAsPrimaryImage(img) {
        if (!img || !img.image || this.isPrimaryImage(img)) {
          return;
        }
        this.editModel.url = img.image;
        this.$message.success(this.$t("productHub.setAsPrimarySuccess"));
      },
      isImageSelected(img) {
        return !!img && this.selectedImageIds.indexOf(img.id) !== -1;
      },
      toggleImageSelect(img) {
        if (!img) {
          return;
        }
        const index = this.selectedImageIds.indexOf(img.id);
        if (index === -1) {
          this.selectedImageIds.push(img.id);
        } else {
          this.selectedImageIds.splice(index, 1);
        }
      },
      toggleSelectAllImages(checked) {
        this.selectedImageIds = checked ? this.detailImages.map(img => img.id) : [];
      },
      async deleteSelectedImages() {
        if (!this.selectedImageIds.length || this.bulkDeletingImages) {
          return;
        }
        const ids = [...this.selectedImageIds];
        try {
          await this.$confirm(this.$t("productHub.deleteSelectedConfirm", { count: ids.length }), this.$t("common.warning"), {
            confirmButtonText: this.$t("common.definite"),
            cancelButtonText: this.$t("common.cancel"),
            type: "warning"
          });
        } catch (e) {
          return;
        }
        this.bulkDeletingImages = true;
        const deletedIds = [];
        let fail = 0;
        for (const id of ids) {
          try {
            const res = await this.$api.request(this.$url.OpenDoor.materialImageDelete, this.$method.delete, { id });
            if (res.code === 0) {
              deletedIds.push(id);
            } else {
              fail += 1;
            }
          } catch (e) {
            fail += 1;
          }
        }
        this.detailImages = this.detailImages.filter(img => deletedIds.indexOf(img.id) === -1);
        this.selectedImageIds = this.selectedImageIds.filter(id => deletedIds.indexOf(id) === -1);
        this.bulkDeletingImages = false;
        if (fail) {
          this.$message.warning(this.$t("productHub.deleteSelectedPartial", { ok: deletedIds.length, fail }));
        } else {
          this.$message.success(this.$t("productHub.deleteSelectedSuccess", { count: deletedIds.length }));
        }
      },
      submitDetailForReview() {
        const goodsId = this.detailItem && (this.detailItem.goodsId || this.detailItem.id);
        if (!goodsId || this.submittingForReview) {
          return;
        }
        this.submittingForReview = true;
        this.$api
          .request(this.$url.OpenDoor.materialSubmitForReview, this.$method.post, { goodsId })
          .then(res => {
            this.submittingForReview = false;
            if (res.code === 0) {
              this.$message.success(this.$t("productHub.submitForReviewSuccess"));
              this.$set(this.detailItem, "goodsOpenDoorSkuDTO", {
                ...(this.detailItem.goodsOpenDoorSkuDTO || {}),
                skuResult: 0
              });
              this.loadCatalog(this.catalogPage.pageIndex);
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.submittingForReview = false;
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      // ================= CSV 批量导入素材库 =================
      openImportCsv() {
        this.importCsvFile = null;
        this.importCsvUploading = false;
        this.importPolling = false;
        this.importBatchId = "";
        this.importResult = null;
        this.importCsvVisible = true;
      },
      closeImportCsv() {
        if (this.importPollTimer) {
          clearTimeout(this.importPollTimer);
          this.importPollTimer = null;
        }
        this.importPolling = false;
      },
      handleImportFileChange(file) {
        this.importCsvFile = file;
      },
      startImportCsv() {
        if (!this.importCsvFile || this.importCsvUploading) {
          return;
        }
        this.importCsvUploading = true;
        this.importResult = null;
        const formData = new FormData();
        formData.append("file", this.importCsvFile.raw || this.importCsvFile);
        this.$api
          .upload(this.$url.ProductLibrary.import, formData)
          .then(res => {
            this.importCsvUploading = false;
            if (res.code === 0) {
              this.importBatchId = res.data;
              this.importResult = { totalRows: 0, successCount: 0, skippedCount: 0, failedCount: 0, details: [], done: false };
              this.pollImportStatus();
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.importCsvUploading = false;
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      pollImportStatus() {
        if (!this.importBatchId) {
          return;
        }
        this.importPolling = true;
        this.$api
          .request(this.$url.ProductLibrary.importStatus, this.$method.get, { batchId: this.importBatchId })
          .then(res => {
            if (res.code === 0) {
              this.importResult = res.data;
              if (!res.data.done && this.importCsvVisible) {
                this.importPollTimer = setTimeout(this.pollImportStatus, 2000);
              } else {
                this.importPolling = false;
                if (res.data.done) {
                  this.loadCatalog(this.catalogPage.pageIndex);
                  this.loadAllTypes();
                }
              }
            } else {
              this.importPolling = false;
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(() => {
            this.importPolling = false;
          });
      },
      // ================= 分类与标签 =================
      loadAllTypes() {
        this.$api.request(this.$url.ProductType.list, this.$method.get).then(res => {
          if (res.code === 0) {
            this.allTypeList = res.data || [];
          }
        });
      },
      listProductTag() {
        this.$api.request(this.$url.ProductTag.list, this.$method.get).then(res => {
          if (res.code === 0) {
            this.productTags = res.data;
          }
        });
      },
      loadTypes(pageIndex) {
        this.typeLoading = true;
        this.typePage.pageIndex = pageIndex ? pageIndex : 1;
        this.$api
          .request(this.$url.ProductType.page, this.$method.get, {
            pageIndex: this.typePage.pageIndex,
            pageSize: this.typePage.pageSize
          })
          .then(res => {
            if (res.code === 0) {
              this.typeList = res.data.records || [];
              this.typePage.totalSize = parseInt(res.data.total) || 0;
            }
            this.typeLoading = false;
          })
          .catch(() => {
            this.typeLoading = false;
          });
      },
      addType() {
        this.productType = { id: "", name: "", notes: "" };
        this.dialogProductType = true;
      },
      editType(row) {
        this.productType = { id: row.id, name: row.name, notes: row.notes };
        this.dialogProductType = true;
      },
      async delType(row) {
        if (!row || !row.id || this.deletingTypeId) {
          return;
        }
        let usageCount = 0;
        try {
          const countRes = await this.$api.request(this.$url.ProductType.usageCount, this.$method.get, { id: row.id });
          usageCount = (countRes.code === 0 && countRes.data) || 0;
        } catch (e) {
          usageCount = 0;
        }
        const message = usageCount > 0 ? this.$t("productHub.deleteTypeInUseConfirm", { count: usageCount }) : this.$t("productHub.deleteTypeConfirm");
        try {
          await this.$confirm(message, this.$t("common.warning"), {
            confirmButtonText: this.$t("common.definite"),
            cancelButtonText: this.$t("common.cancel"),
            type: "warning"
          });
        } catch (e) {
          return;
        }
        this.deletingTypeId = row.id;
        this.$api
          .request(this.$url.ProductType.delete, this.$method.delete, { id: row.id })
          .then(res => {
            this.deletingTypeId = "";
            if (res.code === 0) {
              this.$message.success(this.$t("productHub.deleteTypeSuccess"));
              this.loadTypes(this.typePage.pageIndex);
              this.loadAllTypes();
              this.loadMine(this.minePage.pageIndex);
              this.loadCatalog(this.catalogPage.pageIndex);
            } else {
              this.$message.error(res.msg || this.$t("common.loadFailed"));
            }
          })
          .catch(err => {
            this.deletingTypeId = "";
            this.$message.error(err && err.msg ? err.msg : this.$t("common.loadFailed"));
          });
      },
      cancelTypeDialog(dialog, state) {
        this.dialogProductType = dialog;
        if (state) {
          this.loadTypes(this.typePage.pageIndex);
          this.loadAllTypes();
        }
      },
      loadTags(pageIndex) {
        this.tagLoading = true;
        this.tagPage.pageIndex = pageIndex ? pageIndex : 1;
        this.$api
          .request(this.$url.ProductTag.page, this.$method.get, {
            pageIndex: this.tagPage.pageIndex,
            pageSize: this.tagPage.pageSize
          })
          .then(res => {
            if (res.code === 0) {
              this.tagList = res.data.records || [];
              this.tagPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.tagLoading = false;
          })
          .catch(() => {
            this.tagLoading = false;
          });
      },
      // 共享标签/分类（uId 为 "0"）对运营商只读，只有素材库管理员可以修改/删除（后端同样校验）
      canManageShared(row) {
        return String(row.uId) !== "0" || this.isLibraryAdmin;
      },
      addTag() {
        this.productTag = { id: "", name: "", notes: "" };
        this.dialogProductTag = true;
      },
      editTag(row) {
        this.productTag = { id: row.id, name: row.name, notes: row.notes };
        this.dialogProductTag = true;
      },
      delTag(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.$api
              .request(this.$url.ProductTag.delete, this.$method.delete, { id: row.id })
              .then(res => {
                if (res.code === 0) {
                  this.$message.success(res.msg);
                  this.loadTags(this.tagPage.pageIndex);
                  this.listProductTag();
                }
              });
          })
          .catch(() => {});
      },
      cancelTagDialog(dialog, state) {
        this.dialogProductTag = dialog;
        if (state) {
          this.loadTags(this.tagPage.pageIndex);
          this.listProductTag();
        }
      },
      // ================= 公共格式化 =================
      // packSizeUnit === "ml" 时按 fl oz 展示（换算），其余（如 "g"）直接展示原始值，不做任何换算
      formatWeight(item) {
        const raw = item && Number(item.netWeight);
        if (!raw) {
          return "";
        }
        if (item.packSizeUnit === "ml") {
          return Math.round((raw / ML_PER_FL_OZ) * 100) / 100 + " " + this.$t("productHub.unitFlOz");
        }
        return raw + " " + this.$t("productHub.unitG");
      },
      auditPill(row) {
        if (!row || !row.goodsOpenDoorSkuDTO) {
          return null;
        }
        const skuResult = row.goodsOpenDoorSkuDTO.skuResult;
        if (skuResult === null || skuResult === undefined) {
          return null;
        }
        // 审核状态：0 待提交 / 1 审核中 / 2 已通过 / 3 已驳回
        const map = {
          0: { label: this.$t("productHub.statusPending"), cls: "vtm-pill--off" },
          1: { label: this.$t("productHub.statusAuditing"), cls: "vtm-pill--info" },
          2: { label: this.$t("productHub.statusApproved"), cls: "vtm-pill--ok" },
          3: { label: this.$t("productHub.statusRejected"), cls: "vtm-pill--crit" }
        };
        return map[skuResult] || { label: String(skuResult), cls: "vtm-pill--off" };
      },
      isAudit(row, status) {
        const skuResult = row && row.goodsOpenDoorSkuDTO && row.goodsOpenDoorSkuDTO.skuResult;
        if (skuResult === null || skuResult === undefined) {
          return status === null;
        }
        return skuResult === status;
      },
      rejectReason(row) {
        if (!row || !row.goodsOpenDoorSkuDTO) {
          return "";
        }
        return row.goodsOpenDoorSkuDTO.rejectReasion || "";
      },
      formatPrice(value) {
        if (value === null || value === undefined || value === "") {
          return "-";
        }
        const num = Number(value);
        if (Number.isNaN(num)) {
          return String(value);
        }
        return `$${num.toFixed(2)}`;
      }
    }
  };
</script>

<style scoped lang="scss">
  .product-hub {
    padding-bottom: 56px; // 给吸底批量条留空间
  }

  .hub-head-actions {
    display: flex;
    gap: 8px;
  }

  .hub-seg-row {
    padding: 14px 16px 0;
  }

  .hub-seg ::v-deep .el-radio-button__inner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .hub-seg__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    margin-left: 2px;
    border-radius: 9px;
    background: var(--vtm-well);
    color: var(--vtm-ink-2);
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }

  .hub-seg ::v-deep .is-active .hub-seg__count {
    background: rgba(255, 255, 255, 0.25);
    color: inherit;
  }

  .hub-thumb {
    width: 52px;
    height: 52px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
  }

  .custom-tag {
    margin-right: 5px;
  }

  /* ---------- 素材库图库 ---------- */
  .hub-catalog__toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px 12px;
  }

  .hub-catalog__search {
    width: 260px;
  }

  .hub-catalog__added-filter {
    width: 150px;
  }

  .hub-catalog__hint {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  /* 列表化：每个产品占一行，图片 + 全部字段可见，避免文字被截断 */
  .hub-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .hub-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 14px;
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    cursor: pointer;
    transition: box-shadow var(--vtm-speed) ease, border-color var(--vtm-speed) ease;

    &:hover {
      border-color: var(--vtm-mint-dim);
      box-shadow: var(--vtm-e2);
    }

    &.is-selected {
      border-color: var(--vtm-mint);
      box-shadow: 0 0 0 2px var(--vtm-mint-soft);
    }

    &.is-added {
      opacity: 0.92;
    }
  }

  .hub-card__check {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  .hub-card.is-reviewed {
    background: var(--vtm-well);
  }

  .hub-card__img {
    position: relative;
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    border-radius: var(--vtm-r-sm);
    overflow: hidden;
    background: var(--vtm-well);

    ::v-deep .el-image {
      width: 100%;
      height: 100%;
    }

    ::v-deep .el-image__inner {
      object-fit: contain;
      background: var(--vtm-surface);
    }
  }

  .hub-card__watermark {
    position: absolute;
    top: 9px;
    left: -22px;
    width: 90px;
    transform: rotate(-45deg);
    text-align: center;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #fff;
    background: var(--vtm-ok);
    padding: 1px 0;
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }

  .hub-card__img-ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 24px;
  }

  .hub-card__body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .hub-card__name {
    font-size: 14px;
    font-weight: 600;
    color: var(--vtm-ink);
    line-height: 1.35;
    white-space: normal;
    word-break: break-word;
  }

  .hub-card__verified {
    color: var(--vtm-ok);
    font-size: 18px;
    margin-left: 4px;
    vertical-align: middle;
  }

  .hub-card__sub {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .hub-card__price {
    font-family: var(--vtm-display);
    font-weight: 650;
    font-size: 14px;
    color: var(--vtm-heading);
  }

  .hub-card__chip {
    font-size: 12px;
    color: var(--vtm-ink-2);
    background: var(--vtm-well);
    border-radius: var(--vtm-r-sm);
    padding: 1px 8px;
  }

  .hub-card__chip--mono {
    font-family: var(--vtm-mono);
    letter-spacing: 0.01em;
  }

  .hub-card__added-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--vtm-ok);
    background: var(--vtm-ok-soft);
    border-radius: 12px;
    padding: 1px 9px;
  }

  .hub-card__reviewed-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--vtm-warn);
    background: var(--vtm-warn-soft);
    border: 1px solid var(--vtm-warn-soft);
    border-radius: 12px;
    padding: 1px 9px;
    cursor: pointer;

    &:hover {
      border-color: var(--vtm-warn);
    }

    &.is-on {
      color: var(--vtm-ok);
      background: var(--vtm-ok-soft);
      border: 1px solid var(--vtm-ok-soft);
    }
  }

  .hub-card__flag-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--vtm-ink-3);
    background: var(--vtm-well);
    border: 1px dashed var(--vtm-line);
    border-radius: 12px;
    padding: 1px 9px;
    cursor: pointer;

    &:hover {
      color: var(--vtm-ink-2);
      border-color: var(--vtm-ink-3);
    }

    &.is-on {
      color: var(--vtm-crit);
      background: var(--vtm-crit-soft);
      border: 1px solid var(--vtm-crit-soft);
    }
  }

  .hub-card__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Element 给相邻按钮加了 margin-left，与 flex gap 叠加导致错位；这里归零 */
  .hub-card__actions ::v-deep .el-button {
    margin: 0;
  }

  /* ---------- 批量操作吸底条 ---------- */
  .hub-bulkbar {
    position: fixed;
    left: 50%;
    bottom: 22px;
    transform: translateX(-50%);
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: var(--vtm-navy);
    border-radius: 30px;
    box-shadow: var(--vtm-e3);
  }

  .hub-bulkbar__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    border-radius: 12px;
    background: var(--vtm-mint);
    color: var(--vtm-navy);
    font-size: 12.5px;
    font-weight: 700;
  }

  .hub-bulkbar__clear {
    color: #a9bcce;

    &:hover {
      color: #ffffff;
    }
  }

  .hub-bulk-enter-active,
  .hub-bulk-leave-active {
    transition: all 0.2s ease;
  }

  .hub-bulk-enter,
  .hub-bulk-leave-to {
    opacity: 0;
    transform: translate(-50%, 12px);
  }

  /* ---------- 详情抽屉 ---------- */
  .hub-detail {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .hub-detail__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-bottom: 1px solid var(--vtm-line);
  }

  .hub-detail__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--vtm-display);
    font-size: 15px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      color: var(--vtm-ink-3);
    }
  }

  .hub-detail__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 20px;
  }

  .hub-detail__hero {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .hub-detail__img {
    width: 120px;
    height: 120px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-md);
    border: 1px solid var(--vtm-line);
    background: var(--vtm-well);
  }

  .hub-detail__headline {
    min-width: 0;

    h2 {
      margin: 0 0 8px;
      font-family: var(--vtm-display);
      font-size: 18px;
      font-weight: 650;
      color: var(--vtm-heading);
      line-height: 1.3;
      word-break: break-word;
    }
  }

  .hub-detail__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .hub-detail__kv {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 16px;
    padding: 14px 16px;
    margin-bottom: 16px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface-2);
  }

  .hub-detail__kv-item {
    min-width: 0;
  }

  .hub-detail__kv-label {
    display: block;
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    margin-bottom: 2px;
  }

  .hub-detail__kv-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
    word-break: break-word;
  }

  /* 销售速览 */
  .hub-detail__metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 16px;
  }
  .hub-metric {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 14px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface-2);
  }
  .hub-metric__val {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 700;
    color: var(--vtm-heading);
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hub-metric__label {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }

  /* 内联编辑表单 */
  .hub-edit {
    margin-bottom: 16px;
  }
  .hub-edit__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 14px;
  }
  .hub-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    > span {
      font-size: 11.5px;
      font-weight: 600;
      color: var(--vtm-ink-2);
    }

    .hub-field__hint {
      margin-left: 2px;
      font-size: 12px;
      font-weight: 400;
      color: var(--vtm-ink-3);
      cursor: help;
    }

    ::v-deep .el-select,
    ::v-deep .el-input,
    ::v-deep .el-input-number,
    ::v-deep .el-date-editor {
      width: 100%;
    }
  }
  .hub-field--full {
    grid-column: 1 / -1;
  }
  .hub-edit__section-title {
    margin: 16px 0 10px;
    font-size: 12px;
    font-weight: 700;
    color: var(--vtm-ink-2);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .hub-detail__section {
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    padding: 14px 16px;
  }

  .hub-detail__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-family: var(--vtm-display);
    font-size: 13.5px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      color: var(--vtm-ink-3);
      margin-right: 5px;
    }
  }

  .hub-detail__reviewed-toggle {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: var(--vtm-warn);
    background: var(--vtm-warn-soft);
    border: 1px solid var(--vtm-warn-soft);
    border-radius: 12px;
    padding: 2px 10px;
    cursor: pointer;

    &:hover {
      border-color: var(--vtm-warn);
    }

    &.is-on {
      color: var(--vtm-ok);
      background: var(--vtm-ok-soft);
      border: 1px solid var(--vtm-ok-soft);
    }
  }

  .hub-detail__flag-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: var(--vtm-ink-3);
    background: var(--vtm-well);
    border: 1px dashed var(--vtm-line);
    border-radius: 12px;
    padding: 2px 10px;
    cursor: pointer;

    &:hover {
      color: var(--vtm-ink-2);
      border-color: var(--vtm-ink-3);
    }

    &.is-on {
      color: var(--vtm-crit);
      background: var(--vtm-crit-soft);
      border: 1px solid var(--vtm-crit-soft);
    }
  }

  .hub-detail__flag-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12.5px;
    color: var(--vtm-crit);
    background: var(--vtm-crit-soft);
    border-radius: var(--vtm-r-sm);
    padding: 8px 10px;
    margin-bottom: 12px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .hub-detail__vision-status {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .hub-detail__bulk-imgs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .hub-detail__imgs {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 10px;
  }

  .hub-detail__img-cell {
    width: 100%;
    height: 110px;
    display: block;
    border-radius: var(--vtm-r-sm);
    border: 1px solid var(--vtm-line);
    cursor: pointer;
  }

  .hub-detail__img-wrap {
    position: relative;
    border-radius: var(--vtm-r-sm);
    overflow: hidden;

    &.is-primary {
      box-shadow: 0 0 0 2px var(--vtm-mint);
    }
  }

  .hub-detail__img-badge {
    position: absolute;
    top: 5px;
    left: 5px;
    background: var(--vtm-mint);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 3px 7px;
    border-radius: 8px;
    pointer-events: none;
  }

  .hub-detail__img-toolbar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 7px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.68), rgba(0, 0, 0, 0));

    ::v-deep .el-checkbox__inner {
      background: transparent;
      border-color: #fff;
    }
  }

  .hub-detail__img-action {
    color: #fff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    padding: 2px;

    &.is-set {
      color: var(--vtm-mint-dim, var(--vtm-mint));
      cursor: default;
    }

    &.is-danger:hover {
      color: var(--vtm-crit);
    }

    &.is-loading {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .hub-detail__img-add {
    ::v-deep .el-upload--picture-card {
      width: 100%;
      height: 110px;
      line-height: 110px;
    }
  }

  .hub-detail__noimg {
    font-size: 12.5px;
    color: var(--vtm-ink-3);
    text-align: center;
    padding: 18px 0;
  }

  .hub-detail__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid var(--vtm-line);
  }

  /* ---------- CSV 批量导入 ---------- */
  .hub-import__progress {
    margin-top: 16px;
  }

  .hub-import__counts {
    margin-top: 8px;
    font-size: 12.5px;
    color: var(--vtm-ink-3);
  }

  .hub-import__failed-table {
    margin-top: 12px;
  }

  /* ---------- 分类与标签抽屉 ---------- */
  .hub-organize {
    padding: 0 20px 20px;
  }

  .hub-organize__toolbar {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 10px;
  }

  .hub-organize ::v-deep .el-pagination {
    margin-top: 12px;
    text-align: right;
  }

  .hub-danger {
    color: var(--vtm-crit);
  }

  @media (max-width: 760px) {
    .hub-card {
      flex-wrap: wrap;
    }

    .hub-card__actions {
      flex-basis: 100%;
      justify-content: flex-end;
    }

    .hub-detail__kv {
      grid-template-columns: 1fr;
    }
  }
</style>
